"use client";

import type {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  SyntheticEvent,
  TransitionStartFunction,
} from "react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useTransition,
} from "react";
import {
  captureSnapshot,
  checkSnapshot,
} from "@/actions/tools/capture-snapshot";
import {
  compareSnapshots,
  type CompareSuccess,
} from "@/actions/tools/compare-snapshots";
import { Loader2 } from "lucide-react";
import { DevicePreset } from "@/lib/viewports";
import { Button } from "@/components/ui/button";
import { TurnstileChallenge } from "@/components/security/turnstile-challenge";
import { CapturePanel, ScreenshotState } from "./capture-panel";
import { useTurnstileSession } from "@/hooks/use-turnstile-session";
import { ComparisonViewer } from "@/components/snapshots/comparison-viewer";
import { ToolRegistrationCTA } from "@/components/tools/tool-registration-cta";

type PollControl = { token: number; timer?: ReturnType<typeof setTimeout> };

interface ScreenshotComparisonProps {
  devicePresets: DevicePreset[];
  baselineUrl?: string;
  targetUrl?: string;
  initialViewport?: string;
}

export function ScreenshotComparison({
  devicePresets,
  baselineUrl,
  targetUrl,
  initialViewport,
}: ScreenshotComparisonProps) {
  const POLL_INTERVAL_MS = 2000;
  const POLL_TIMEOUT_MS = 120000;

  const getDefaultDevice = () => {
    if (initialViewport) {
      const found = devicePresets.find((d) => d.id === initialViewport);
      if (found) return found.id;
    }
    return "macbook-pro-14";
  };

  const defaultDevice = getDefaultDevice();

  const [leftState, setLeftState] = useState<ScreenshotState>({
    url: baselineUrl ?? "",
    device: defaultDevice,
  });
  const [rightState, setRightState] = useState<ScreenshotState>({
    url: targetUrl ?? "",
    device: defaultDevice,
  });

  const [isLeftPending, startLeftTransition] = useTransition();
  const [isRightPending, startRightTransition] = useTransition();

  const [isLeftPolling, setIsLeftPolling] = useState(false);
  const [isRightPolling, setIsRightPolling] = useState(false);

  const leftPollControl = useRef<PollControl>({ token: 0 });
  const rightPollControl = useRef<PollControl>({ token: 0 });

  const [comparisonResult, setComparisonResult] =
    useState<CompareSuccess | null>(null);
  const [comparisonError, setComparisonError] = useState<string>();
  const [isComparing, setIsComparing] = useState(false);

  const { isReady: isTurnstileReady } = useTurnstileSession();
  const turnstileCaptureMessage =
    "Complete the Turnstile challenge to capture fresh screenshots.";

  const syncRightDevice = (deviceId: string) => {
    let deviceChanged = false;
    setRightState((previous) => {
      if (previous.device === deviceId) {
        return previous;
      }

      deviceChanged = true;
      return {
        ...previous,
        device: deviceId,
        screenshotUrl: undefined,
        snapshotId: undefined,
        status: undefined,
        error: undefined,
      };
    });

    if (!deviceChanged) {
      return;
    }

    if (rightPollControl.current.timer) {
      clearTimeout(rightPollControl.current.timer);
      rightPollControl.current.timer = undefined;
    }
    rightPollControl.current.token += 1;
    setIsRightPolling(false);
  };

  const setLeftStateWithSync: Dispatch<SetStateAction<ScreenshotState>> = (
    update,
  ) => {
    setLeftState((previous) => {
      const next =
        typeof update === "function"
          ? (update as (prev: ScreenshotState) => ScreenshotState)(previous)
          : update;
      syncRightDevice(next.device);
      return next;
    });
  };

  const handleLeftPanelStateChange = (next: ScreenshotState) => {
    setLeftStateWithSync(next);
  };

  const handleRightPanelStateChange = (next: ScreenshotState) => {
    setRightState({
      ...next,
      device: leftState.device,
    });
  };

  useEffect(() => {
    const leftControl = leftPollControl.current;
    const rightControl = rightPollControl.current;
    return () => {
      if (leftControl.timer) {
        clearTimeout(leftControl.timer);
      }
      if (rightControl.timer) {
        clearTimeout(rightControl.timer);
      }
    };
  }, []);

  const hasBothScreenshots = Boolean(
    leftState.screenshotUrl && rightState.screenshotUrl,
  );

  const canCompare =
    leftState.status === "COMPLETE" &&
    rightState.status === "COMPLETE" &&
    Boolean(leftState.snapshotId && rightState.snapshotId);

  useEffect(() => {
    setComparisonResult(null);
    setComparisonError(undefined);
  }, [leftState.snapshotId, rightState.snapshotId]);

  const schedulePoll = (
    setState: Dispatch<SetStateAction<ScreenshotState>>,
    setIsPolling: Dispatch<SetStateAction<boolean>>,
    pollControl: MutableRefObject<PollControl>,
    token: number,
    snapshotId: string,
    startedAt: number,
  ) => {
    pollControl.current.timer = setTimeout(async () => {
      if (pollControl.current.token !== token) {
        return;
      }

      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        pollControl.current.timer = undefined;
        setIsPolling(false);
        setState((previous) => ({
          ...previous,
          error: "Timed out waiting for screenshot",
          status: undefined,
          snapshotId: undefined,
          screenshotUrl: undefined,
        }));
        return;
      }

      try {
        const result = await checkSnapshot({
          snapshotId,
          timeout: POLL_INTERVAL_MS,
        });

        if (pollControl.current.token !== token) {
          return;
        }

        if (result.success) {
          setState((previous) => ({
            ...previous,
            snapshotId: result.snapshotId,
            status: result.status,
            screenshotUrl: result.screenshotUrl ?? previous.screenshotUrl,
            error: undefined,
          }));

          if (result.status === "COMPLETE") {
            pollControl.current.timer = undefined;
            setIsPolling(false);
            return;
          }

          schedulePoll(
            setState,
            setIsPolling,
            pollControl,
            token,
            snapshotId,
            startedAt,
          );
        } else {
          pollControl.current.timer = undefined;
          setIsPolling(false);
          setState((previous) => ({
            ...previous,
            error: result.error,
            status: undefined,
            snapshotId: undefined,
            screenshotUrl: undefined,
          }));
        }
      } catch (error) {
        if (pollControl.current.token !== token) {
          return;
        }

        pollControl.current.timer = undefined;
        setIsPolling(false);
        setState((previous) => ({
          ...previous,
          error: error instanceof Error ? error.message : "Unexpected error",
          status: undefined,
          snapshotId: undefined,
          screenshotUrl: undefined,
        }));
      }
    }, POLL_INTERVAL_MS);
  };

  const handleCapture = (
    currentState: ScreenshotState,
    setState: Dispatch<SetStateAction<ScreenshotState>>,
    setIsPolling: Dispatch<SetStateAction<boolean>>,
    startTransition: TransitionStartFunction,
    pollControl: MutableRefObject<PollControl>,
  ) => {
    const normalizedUrl = currentState.url.trim();
    const deviceId = currentState.device;
    const device = devicePresets.find((d) => d.id === deviceId);

    if (!device) {
      setState((previous) => ({
        ...previous,
        error: "Select a device before capturing.",
      }));
      return;
    }

    if (!normalizedUrl) {
      setState((previous) => ({
        ...previous,
        error: "Provide a public URL before capturing.",
      }));
      return;
    }

    if (!isTurnstileReady) {
      setState((previous) => ({
        ...previous,
        error: "Complete the Turnstile challenge before capturing.",
      }));
      return;
    }

    const nextToken = pollControl.current.token + 1;
    pollControl.current.token = nextToken;
    if (pollControl.current.timer) {
      clearTimeout(pollControl.current.timer);
      pollControl.current.timer = undefined;
    }

    setIsPolling(false);

    setState((previous) => ({
      ...previous,
      url: normalizedUrl,
      device: deviceId,
      error: undefined,
      screenshotUrl: undefined,
      snapshotId: undefined,
      status: undefined,
    }));

    startTransition(() => {
      captureSnapshot({
        url: normalizedUrl,
        viewport: {
          width: device.width,
          height: device.height,
          deviceScaleFactor: device.deviceScaleFactor ?? 1,
        },
        options: {
          format: "png",
          block: {
            cookieBanners: true,
            chats: true,
            ads: true,
            trackers: true,
          }
        },
      })
        .then((result) => {
          if (pollControl.current.token !== nextToken) {
            return;
          }

          if (result.success) {
            setState((previous) => ({
              ...previous,
              snapshotId: result.snapshotId,
              status: result.status,
              screenshotUrl: result.screenshotUrl ?? previous.screenshotUrl,
              error: undefined,
            }));

            if (result.status === "COMPLETE") {
              setIsPolling(false);
              return;
            }

            setIsPolling(true);
            schedulePoll(
              setState,
              setIsPolling,
              pollControl,
              nextToken,
              result.snapshotId,
              Date.now(),
            );
          } else {
            setIsPolling(false);
            setState((previous) => ({
              ...previous,
              error: result.error,
              snapshotId: undefined,
              status: undefined,
              screenshotUrl: undefined,
            }));
          }
        })
        .catch((error) => {
          if (pollControl.current.token !== nextToken) {
            return;
          }

          setIsPolling(false);
          setState((previous) => ({
            ...previous,
            error: error instanceof Error ? error.message : "Unexpected error",
            snapshotId: undefined,
            status: undefined,
            screenshotUrl: undefined,
          }));
        });
    });
  };

  const captureLeft = () => {
    handleCapture(
      leftState,
      setLeftStateWithSync,
      setIsLeftPolling,
      startLeftTransition,
      leftPollControl,
    );
  };

  const captureRight = () => {
    handleCapture(
      rightState,
      setRightState,
      setIsRightPolling,
      startRightTransition,
      rightPollControl,
    );
  };

  const handleCompare = async () => {
    if (!canCompare || !leftState.snapshotId || !rightState.snapshotId) {
      return;
    }

    try {
      setIsComparing(true);
      setComparisonError(undefined);
      const result = await compareSnapshots({
        baseline: leftState.snapshotId,
        target: rightState.snapshotId,
      });

      if (result.success) {
        setComparisonResult(result);
      } else {
        setComparisonResult(null);
        setComparisonError(result.error);
      }
    } catch (error) {
      setComparisonResult(null);
      setComparisonError(
        error instanceof Error ? error.message : "Unexpected error",
      );
    } finally {
      setIsComparing(false);
    }
  };

  const detail = comparisonResult?.detail;
  const summary = comparisonResult?.summary;
  const helperText = (() => {
    if (!hasBothScreenshots) {
      return "Capture both screenshots to enable the comparison button.";
    }
    if (!canCompare) {
      return "Waiting for both screenshots to finish processing...";
    }
    return "Retrieve disturbance scores and comparison views once both screenshots are ready.";
  })();

  return (
    <div className="space-y-8">
      <TurnstileChallenge />
      <div className="grid gap-6 lg:grid-cols-2">
        <CapturePanel
          title="Baseline screenshot"
          state={leftState}
          onStateChange={handleLeftPanelStateChange}
          onCapture={captureLeft}
          devicePresets={devicePresets}
          isPending={isLeftPending || isLeftPolling}
          isChallengeReady={isTurnstileReady}
          challengeHelperText={turnstileCaptureMessage}
          deviceInfoHint="Register for a free account to save baseline screenshots for future comparisons."
        />
        <CapturePanel
          title="Target screenshot"
          state={rightState}
          onStateChange={handleRightPanelStateChange}
          onCapture={captureRight}
          devicePresets={devicePresets}
          isPending={isRightPending || isRightPolling}
          canSelectDevice={false}
          isChallengeReady={isTurnstileReady}
          challengeHelperText={turnstileCaptureMessage}
          deviceInfoHint="Viewport matches the baseline capture to enable accurate comparisons."
        />
      </div>

      <div className="rounded-xl border bg-muted/40 p-6 shadow-sm">
        <div className="flex flex-col gap-3">
          <Button
            disabled={!canCompare || isComparing}
            className="w-full"
            size="lg"
            onClick={handleCompare}
          >
            {isComparing ? (
              <span className="flex items-center gap-2">
                <Loader2 className="size-4 animate-spin" />
                Comparing...
              </span>
            ) : (
              "Compare"
            )}
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            {helperText}
          </p>
          {comparisonError ? (
            <p className="text-sm text-destructive">{comparisonError}</p>
          ) : null}
        </div>
      </div>

      {comparisonResult && detail && summary ? (
        <>
          <ComparisonViewer
            baselineUrl={detail.screenshots?.baseline ?? ""}
            targetUrl={detail.screenshots?.target ?? ""}
            diffUrl={detail.screenshots?.diff}
            summary={summary}
            viewport={detail.viewport}
          />
          <ToolRegistrationCTA
            title="Need ongoing visual monitoring?"
            description="Create a free account to save comparisons and set up automated visual regression testing."
          />
        </>
      ) : null}
    </div>
  );
}
